import { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { CreditCard, Upload, CheckCircle2, Loader2, FileText, ShieldCheck, X, AlertCircle, MessageSquare, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const pub = axios.create({ baseURL: API });

const ALLOWED = ["jpg", "jpeg", "png", "pdf", "docx"];
const MAX_BYTES = 25 * 1024 * 1024;

export default function ActionPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [uploads, setUploads] = useState({}); // name -> {progress, status}
  const [note, setNote] = useState("");
  const [savingNote, setSavingNote] = useState(false);
  const [noteSaved, setNoteSaved] = useState(false);
  const noteInit = useRef(false);
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try { const { data } = await pub.get(`/public/action/${token}`); setData(data); }
    catch (e) { setError(e.response?.data?.detail || "This link is not available."); }
  }, [token]);

  useEffect(() => { load(); }, [load]);
  // Realtime: instant updates via WebSocket (polling below remains the fallback)
  useEffect(() => {
    let ws;
    try {
      const base = (process.env.REACT_APP_BACKEND_URL || "").replace(/^http/, "ws");
      ws = new WebSocket(`${base}/api/ws/action/${token}`);
      ws.onmessage = (e) => { try { const m = JSON.parse(e.data); if (m.type === "action_update") setData(m.data); } catch { /* ignore */ } };
    } catch { /* ignore */ }
    return () => { try { ws && ws.close(); } catch { /* ignore */ } };
  }, [token]);
  // seed the note box once from any note already saved for this link
  useEffect(() => {
    if (data && !noteInit.current) { setNote(data.customer_note || ""); noteInit.current = true; }
  }, [data]);
  // Poll for payment status while a payment is pending
  useEffect(() => {
    const pay = data?.actions?.payment;
    if (pay?.enabled && pay.status !== "paid" && !data?.expired) {
      pollRef.current = setInterval(load, 6000);
      return () => clearInterval(pollRef.current);
    }
  }, [data, load]);

  const onFiles = async (files) => {
    for (const file of Array.from(files)) {
      const ext = file.name.split(".").pop().toLowerCase();
      if (!ALLOWED.includes(ext)) { setUploads((u) => ({ ...u, [file.name]: { status: "error", msg: "Unsupported type" } })); continue; }
      if (file.size > MAX_BYTES) { setUploads((u) => ({ ...u, [file.name]: { status: "error", msg: "Over 25 MB" } })); continue; }
      setUploads((u) => ({ ...u, [file.name]: { progress: 0, status: "uploading" } }));
      const fd = new FormData();
      fd.append("file", file);
      try {
        await pub.post(`/public/action/${token}/upload`, fd, {
          onUploadProgress: (e) => {
            const p = Math.round((e.loaded / (e.total || file.size)) * 100);
            setUploads((u) => ({ ...u, [file.name]: { progress: p, status: "uploading" } }));
          },
        });
        setUploads((u) => ({ ...u, [file.name]: { progress: 100, status: "done" } }));
      } catch (e) {
        setUploads((u) => ({ ...u, [file.name]: { status: "error", msg: e.response?.data?.detail || "Failed" } }));
      }
    }
    load();
  };

  const saveNote = async () => {
    setSavingNote(true); setNoteSaved(false);
    try {
      await pub.post(`/public/action/${token}/note`, { note });
      setNoteSaved(true);
      setTimeout(() => setNoteSaved(false), 2500);
    } catch (e) { /* keep it quiet on the public page */ }
    finally { setSavingNote(false); }
  };

  if (error) return <Shell><Center><AlertCircle className="h-10 w-10 text-rose-400" /><p className="mt-3 text-slate-600">{error}</p></Center></Shell>;
  if (!data) return <Shell><Center><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></Center></Shell>;

  const pay = data.actions?.payment || {};
  const up = data.actions?.file_upload || {};
  const allDone = (!pay.enabled || pay.status === "paid") && (!up.enabled || up.status === "received");

  return (
    <Shell>
      <div className="w-full max-w-md mx-auto" data-testid="action-page">
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-1.5 mb-3">
            <img src="/operamind-mark.png" alt="" className="h-8 w-8 object-contain" onError={(e) => (e.target.style.display = "none")} />
            <span className="font-heading font-extrabold text-lg tracking-tight text-slate-900">{data.business_name}</span>
          </div>
          <p className="text-slate-500 text-sm">{data.customer_name ? `Hi ${data.customer_name}, ` : ""}please complete the steps below.</p>
        </div>

        {data.expired ? (
          <Card><Center><AlertCircle className="h-9 w-9 text-amber-400" /><p className="mt-3 text-slate-600">This link has expired. Please ask for a new one.</p></Center></Card>
        ) : allDone ? (
          <Card><Center><CheckCircle2 className="h-12 w-12 text-emerald-500" data-testid="action-complete" /><p className="mt-3 font-semibold text-slate-800">All done — thank you!</p><p className="text-sm text-slate-500 mt-1">Everything's been received.</p>
            {up.enabled && (up.uploaded || []).length > 0 && (
              <div className="mt-4 w-full space-y-2">
                {up.uploaded.map((f) => (
                  <div key={f.id} className="flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 rounded-lg px-3 py-2">
                    <FileText className="h-4 w-4" /> <span className="truncate flex-1 text-left">{f.name}</span> <CheckCircle2 className="h-4 w-4" />
                  </div>
                ))}
              </div>
            )}
          </Center></Card>
        ) : (
          <div className="space-y-4">
            {pay.enabled && (
              <Card>
                <div className="flex items-center gap-2 mb-3"><CreditCard className="h-5 w-5 text-blue-600" /><h2 className="font-semibold text-slate-800">Payment</h2></div>
                <p className="text-sm text-slate-500">{pay.description}</p>
                <p className="text-3xl font-bold text-slate-900 mt-1">{pay.currency || "GBP"} {Number(pay.amount).toFixed(2)}</p>
                {pay.status === "paid" ? (
                  <div className="mt-4 flex items-center gap-2 text-emerald-600 font-medium" data-testid="payment-paid"><CheckCircle2 className="h-5 w-5" /> Payment received</div>
                ) : (
                  <>
                    <a href={pay.url} target="_blank" rel="noreferrer" data-testid="pay-now-btn"
                      className="mt-4 block text-center bg-blue-600 hover:bg-blue-700 text-white rounded-xl py-3.5 font-semibold transition-colors">
                      Pay {pay.currency || "GBP"} {Number(pay.amount).toFixed(2)} securely
                    </a>
                    <p className="text-xs text-slate-400 mt-2 flex items-center gap-1 justify-center"><Loader2 className="h-3 w-3 animate-spin" /> Waiting for payment — this updates automatically.</p>
                  </>
                )}
              </Card>
            )}

            {up.enabled && (
              <Card>
                <div className="flex items-center gap-2 mb-2"><Upload className="h-5 w-5 text-blue-600" /><h2 className="font-semibold text-slate-800">Upload documents</h2></div>
                <p className="text-sm text-slate-500">{up.prompt}</p>
                <button data-testid="upload-dropzone" onClick={() => fileRef.current?.click()}
                  className="mt-3 w-full border-2 border-dashed border-slate-200 rounded-xl py-8 text-center hover:border-blue-400 transition-colors">
                  <Upload className="h-6 w-6 text-slate-400 mx-auto" />
                  <p className="text-sm text-slate-600 mt-2 font-medium">Tap to choose files</p>
                  <p className="text-xs text-slate-400 mt-0.5">JPG, PNG, PDF, DOCX · up to 25 MB</p>
                </button>
                <input ref={fileRef} data-testid="file-input" type="file" multiple accept=".jpg,.jpeg,.png,.pdf,.docx" className="hidden"
                  onChange={(e) => onFiles(e.target.files)} />

                {(up.uploaded || []).map((f) => (
                  <div key={f.id} className="mt-2 flex items-center gap-2 text-sm bg-emerald-50 text-emerald-700 rounded-lg px-3 py-2">
                    <FileText className="h-4 w-4" /> <span className="truncate flex-1">{f.name}</span> <CheckCircle2 className="h-4 w-4" />
                  </div>
                ))}
                {Object.entries(uploads).filter(([, s]) => s.status !== "done").map(([name, s]) => (
                  <div key={name} className="mt-2 text-sm bg-slate-50 rounded-lg px-3 py-2">
                    <div className="flex items-center gap-2"><FileText className="h-4 w-4 text-slate-400" /><span className="truncate flex-1">{name}</span>
                      {s.status === "error" ? <span className="text-rose-500 text-xs flex items-center gap-1"><X className="h-3.5 w-3.5" />{s.msg}</span> : <span className="text-xs text-slate-500">{s.progress}%</span>}
                    </div>
                    {s.status === "uploading" && <div className="mt-1.5 h-1.5 bg-slate-200 rounded-full overflow-hidden"><div className="h-full bg-blue-600 transition-all" style={{ width: `${s.progress}%` }} /></div>}
                  </div>
                ))}
              </Card>
            )}
          </div>
        )}

        {!data.expired && data.status !== "revoked" && (
          <Card data-testid="notes-card" className="mt-4">
            <div className="flex items-center gap-2 mb-2"><MessageSquare className="h-5 w-5 text-blue-600" /><h2 className="font-semibold text-slate-800">Additional notes</h2></div>
            <p className="text-sm text-slate-500">Anything you'd like {data.business_name} to know? Leave your instructions here.</p>
            <textarea data-testid="notes-input" value={note} onChange={(e) => { setNote(e.target.value); setNoteSaved(false); }} rows={3}
              placeholder="e.g. Please call before you arrive, gate code is 1234…"
              className="mt-3 w-full rounded-xl border border-slate-200 px-3 py-2.5 text-sm focus:outline-none focus:border-blue-400" />
            <button data-testid="save-notes-btn" onClick={saveNote} disabled={savingNote}
              className="mt-2 w-full flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white rounded-xl py-3 font-semibold transition-colors disabled:opacity-60">
              {savingNote ? <Loader2 className="h-4 w-4 animate-spin" /> : noteSaved ? <><CheckCircle2 className="h-4 w-4" /> Saved</> : <><Send className="h-4 w-4" /> Save note</>}
            </button>
          </Card>
        )}

        <p className="text-center text-xs text-slate-400 mt-6 flex items-center gap-1 justify-center"><ShieldCheck className="h-3.5 w-3.5" /> Secured by OperaMind</p>
      </div>
    </Shell>
  );
}

const Shell = ({ children }) => <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">{children}</div>;
const Card = ({ children, className = "", ...rest }) => <div {...rest} className={`rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ${className}`}>{children}</div>;
const Center = ({ children }) => <div className="flex flex-col items-center text-center py-6">{children}</div>;
